/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import moment from "moment";
import { useEffect, useState } from "react";
import { DateRangePicker } from "react-date-range";
import { Helmet } from "react-helmet";
import ReactHtmlParser from "react-html-parser";
import Select from "react-select";
import ReactPaginate from "react-paginate";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Dialog, DialogBody } from "@material-tailwind/react";

/* ICONS */
import { FaCalendar } from "react-icons/fa6";
import { HiOutlineDotsVertical } from "react-icons/hi";
import { IoClose } from "react-icons/io5";

/* USER DEFINED COMPONENTS */
import InputField from "components/input";
import { Link } from "react-router-dom";
import {
  deleteBlog,
  editBlogById,
  getBlogList,
  getCategoryBlogList,
} from "service/api";
import { IMAGE_URL } from "config";

const Blogs = () => {
  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [BlogsList, setBlogsList] = useState([]);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [totalDocs, setTotalDocs] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);

  /* FUNCTIONS */
  const handlePageChange = (e) => {
    setPage(e);
    // No need to call getBlogListFunction() here — the useEffect watches `page` and will re-fetch automatically
  };

  const handlePerPageChange = (e) => {
    setSizePerPage(e);
    setPage(1);
    // No need to call getBlogListFunction() here — the useEffect watches `sizePerPage` and will re-fetch automatically
  };

  const getBlogListFunction = () => {
    setLoading(true);
    const params = {
      page: page,
      sizePerPage: sizePerPage,
    };
    if (search) {
      params.search = search;
    }
    if (selectedCategory?.value) {
      params.category = selectedCategory.value;
    }

    /* HANDLE GET BLOG DATA */
    getBlogList(params)
      .then((res) => {
        setBlogsList(res?.data?.docs);
        setTotalDocs(res?.data?.totalDocs);
      })
      .catch((e) => {
        console.log("Error: ", e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* HANDLE DELETE DATA */
  const handleDelete = (deleteId) => {
    deleteBlog(deleteId)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        setDeleteModal(!deleteModal);
        setDeleteId(null);
        getBlogListFunction();
      });
  };

  /* HANDLE STATUS CHANGE FUNCTION */
  const handleStatusChange = (editId) => {
    editBlogById(editId)
      .then((res) => {
        toast.success(res?.message);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.message);
      })
      .finally(() => {
        getBlogListFunction();
      });
  };

  /* USE-EFFECTS */
  useEffect(() => {
    getBlogListFunction();
  }, [page, sizePerPage, search, selectedCategory?.value]);

  useEffect(() => {
    getCategoryBlogList({ pagination: false })
      .then((res) => {
        if (res?.status) {
          const options = (res?.data || []).map((item) => ({
            value: item?._id,
            label: item?.name,
          }));
          setCategoryOptions(options);
        }
      })
      .catch((e) => {
        console.log("Error: ", e);
      });
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest("[data-blog-menu='true']")) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* LOADING SCREEN FOR DATATABLE */
  const LoadingScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          Please wait!
        </p>
        <p className="text-sm font-ibm-semibold text-black text-center">
          We are fetching data{" "}
        </p>
      </div>
    );
  };

  /* NO DATA SCREEN FOR DATATABLE */
  const NoDataScreen = () => {
    return (
      <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
        <p className="text-sm font-ibm-semibold text-black text-center">
          There are no queries to display.
        </p>
      </div>
    );
  };

  return (
    <section className="px-0 py-2 xl:p-5 lg:p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Members | Farmers Insurance</title>
      </Helmet>

      <div className="container-full-width">
        <div className="block xl:flex lg:flex items-center justify-between">
          {/* HEADING */}
          <div>
            <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
              Blogs
            </h1>
            <h2 className="text-base font-ibm-regular text-greyText">
              Manage all the blogs going on in the website.
            </h2>
          </div>

          {/* SEARCH AND FILTERS */}
          <div className="block xl:flex lg:flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
            {/* SEARCHBAR */}
            <div className="w-full xl:w-searchBar lg:w-searchBar">
              <InputField
                type="text"
                placeholder="Search..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => {
                  setSearch(e?.target?.value);
                }}
              />
            </div>

            {/* CATEGORY FILTER */}
            <div className="w-full xl:w-60 lg:w-60 mt-2 xl:mt-0 lg:mt-0">
              <Select
                options={categoryOptions}
                placeholder="Filter by category"
                classNamePrefix="dropdown"
                value={selectedCategory}
                onChange={(option) => {
                  setSelectedCategory(option);
                  setPage(1);
                }}
                isClearable
              />
            </div>

            {/* ADD BLOG */}
            <Link to="/blog/add-blog">
              <Button className="w-full xl:w-auto lg:w-auto mt-2 xl:mt-0 lg:mt-0 bg-logoYellow rounded-md py-3 px-8 text-sm font-ibm-semibold shadow-none hover:shadow-none normal-case hover:bg-opacity-80 transition-all duration-300 ease-in-out">
                Add Blog
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-10">
          {loading ? (
            <LoadingScreen />
          ) : BlogsList.length < 1 ? (
            <NoDataScreen />
          ) : (
            <>
              <div className="grid gap-4 lg:grid-cols-3 xl:grid-cols-4 md:grid-cols-2 grid-cols-1">
                {BlogsList.map((blog, index) => (
                  <div
                    key={index}
                    className="bg-white shadow rounded-lg p-5 relative flex flex-col"
                  >
                    <img
                      src={IMAGE_URL + blog?.bannerImage}
                      className="w-full h-40 rounded-md object-cover border border-greyBorder"
                      alt={blog?.title}
                    />

                    <div className="mt-5 flex-1">
                      <h5 className="text-base font-ibm-bold text-black line-clamp-2">
                        {blog?.title}
                      </h5>
                      <div className="text-sm font-ibm-regular text-greyText mt-1 line-clamp-3">
                        {ReactHtmlParser(blog?.description)}
                      </div>
                    </div>

                    <div
                      className="absolute top-6 right-6"
                      data-blog-menu="true"
                    >
                      <Button
                        onClick={() =>
                          setOpenMenuId(
                            openMenuId === blog?._id ? null : blog?._id,
                          )
                        }
                        className="p-2 bg-white rounded-full border border-gray-300 hover:border-black-500 shadow-none hover:shadow-none transition-all duration-300 ease-in-out"
                      >
                        <HiOutlineDotsVertical className="w-4 h-4 text-black" />
                      </Button>

                      {openMenuId === blog?._id && (
                        <div className="absolute right-0 mt-2 w-28 rounded-md border border-blue-gray-50 bg-white p-2 text-sm shadow-lg shadow-blue-gray-500/10 z-10">
                          <Link
                            to={`/blog/edit-blog/${blog?._id}`}
                            className="block font-ibm-regular rounded-md px-3 py-2 text-start text-blue-gray-900 hover:bg-blue-gray-50"
                          >
                            Edit
                          </Link>
                          <button
                            type="button"
                            className="block font-ibm-regular w-full rounded-md px-3 py-2 text-start text-red-500 hover:bg-red-50"
                            onClick={() => {
                              setDeleteId(blog?._id);
                              setDeleteModal(true);
                              setOpenMenuId(null);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {totalDocs > sizePerPage && (
                <div className="mt-8 flex justify-center">
                  <ReactPaginate
                    breakLabel="..."
                    nextLabel="Next >"
                    onPageChange={(e) => handlePageChange(e.selected + 1)}
                    pageRangeDisplayed={3}
                    pageCount={Math.ceil(totalDocs / sizePerPage)}
                    previousLabel="< Previous"
                    renderOnZeroPageCount={null}
                    containerClassName="flex items-center gap-2 font-ibm-regular text-sm"
                    pageClassName="block border border-gray-300 rounded hover:bg-gray-100 transition-all duration-300 text-black"
                    pageLinkClassName="px-3 py-1.5 block cursor-pointer"
                    previousClassName="block border border-gray-300 rounded hover:bg-gray-100 transition-all duration-300 text-black"
                    previousLinkClassName="px-3 py-1.5 block cursor-pointer"
                    nextClassName="block border border-gray-300 rounded hover:bg-gray-100 transition-all duration-300 text-black"
                    nextLinkClassName="px-3 py-1.5 block cursor-pointer"
                    activeClassName="bg-logoYellow text-white border-logoYellow hover:bg-logoYellow font-ibm-bold"
                    disabledClassName="opacity-50 cursor-not-allowed hover:bg-transparent"
                    forcePage={page - 1}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* DELETE BLOG */}
      <Dialog
        open={deleteModal}
        handler={() => setDeleteModal(!deleteModal)}
        size="xs"
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <DialogBody className="p-0">
          <div className="border-b border-authBorder px-5 py-3 flex items-center justify-between">
            <h5 className="text-lg font-ibm-bold text-black tracking-tight">
              Delete
            </h5>
            <Button
              className="p-2 rounded-full bg-white shadow-none hover:shadow-none hover:bg-black hover:bg-opacity-5"
              onClick={() => setDeleteModal(!deleteModal)}
            >
              <IoClose className="w-4 h-4 text-greyText" />
            </Button>
          </div>
          <div className="p-5 border-b border-authBorder">
            <p className="text-base font-ibm-regular text-black">
              Are you sure you want to delete this blog?
            </p>
          </div>
          <div className="flex items-center justify-end px-5 py-3">
            <Button
              onClick={() => handleDelete(deleteId)}
              className="text-sm shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md font-ibm-semibold text-white transition-all duration-300 ease-in-out normal-case"
            >
              Confirm, delete
            </Button>
          </div>
        </DialogBody>
      </Dialog>
    </section>
  );
};

export default Blogs;
